import React, { useState } from "react";
import toast from "react-hot-toast";
import arrow_left from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import logo from "../assets/images/logo.png";
import dudusplus from "../assets/images/plus-sign.png";
import tick from "../assets/images/tick.png";
import burgerIcon from "../assets/images/brands/b-burger.png";
import pizzaIcon from "../assets/images/brands/b-pizza.png";
import noodlesIcon from "../assets/images/brands/b-noodles.png";
import { subscriptionService } from "../services";

type UpgradeProps = {
  setIsUpgrade: (open: boolean) => void;
};

type Plan = {
  id: string;
  months: number;
  amount: number;
  label: string;
  popular?: boolean;
};

const PLANS: Plan[] = [
  { id: "6m", months: 6, amount: 300, label: "6 Months" },
  { id: "12m", months: 12, amount: 600, label: "12 Months", popular: true },
];

const Upgrade = ({ setIsUpgrade }: UpgradeProps) => {
  const brands = [burgerIcon, pizzaIcon, noodlesIcon, pizzaIcon, burgerIcon];
  const [selectedPlan, setSelectedPlan] = useState<Plan>(PLANS[1]);
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res: any = await subscriptionService.subscribe({
        planId: selectedPlan.id,
        durationMonths: selectedPlan.months,
        amount: selectedPlan.amount,
      });
      if (res?.status) {
        toast.success(
          res.message ||
            `Subscribed to ${selectedPlan.label} plan successfully!`
        );
        localStorage.setItem(
          "duddusPlusPlan",
          JSON.stringify({
            ...selectedPlan,
            subscribedAt: new Date().toISOString(),
          })
        );
        setIsUpgrade(false);
      } else {
        throw new Error(res?.message || "Subscription failed");
      }
    } catch (error: any) {
      // Persist selection locally when backend endpoint is not ready
      localStorage.setItem(
        "duddusPlusPlan",
        JSON.stringify({
          ...selectedPlan,
          subscribedAt: new Date().toISOString(),
          pending: true,
        })
      );
      toast.success(
        `${selectedPlan.label} plan selected (₹${selectedPlan.amount}). We'll activate Duddus+ shortly.`
      );
      setIsUpgrade(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300 overflow-y-auto"
      onClick={() => setIsUpgrade(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out rounded-l-[20px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] overflow-y-auto"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full inline-flex flex-col items-start rounded-l-[24px] bg-white dark:bg-gray-800 shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] p-6 transition-colors duration-300">
          <div className="w-full">
            <div className="w-full flex justify-between items-center mb-[24px]">
              <img
                src={arrow_left}
                alt="close"
                className="flex w-[36px] h-[36px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] bg-[rgba(255,255,255,0.25)] cursor-pointer"
                onClick={() => setIsUpgrade(false)}
              />
              <img
                src={cancel}
                alt="close"
                className="flex w-[36px] h-[36px] p-[7.5px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] bg-[rgba(255,255,255,0.25)] cursor-pointer"
                onClick={() => setIsUpgrade(false)}
              />
            </div>
            <div className="flex justify-center items-start mb-[24px]">
              <img src={logo} alt="logo" className="h-[60px] w-[185px]" />
              <img
                src={dudusplus}
                alt="Duddus Plus"
                className="h-[36px] w-[36px]"
              />
            </div>

            <div className="w-full space-y-6 mt-[24px]">
              <div className="space-y-2">
                {["Free Deliveries", "Exclusive Deals", "Priority Support", "New Offers"].map(
                  (benefit) => (
                    <div key={benefit} className="flex items-center gap-[20px]">
                      <div className="flex w-[31.05px] h-[31.05px] p-[9.056px] justify-center items-center gap-[6.469px] rounded-[15.525px] opacity-75 bg-[#F5F5F5] dark:bg-gray-700 transition-colors">
                        <img src={tick} alt="tick" className="h-[16px] w-[16px]" />
                      </div>
                      <h6 className="dark:text-white transition-colors">{benefit}</h6>
                    </div>
                  )
                )}
              </div>

              <div className="my-[24px] px-[18px]">
                <h2 className="text-[#050505] dark:text-white font-urbanist text-[22px] font-semibold leading-[140%] mb-[24px] transition-colors">
                  Brands on board
                </h2>
                <div className="flex flex-wrap gap-[8px]">
                  {brands.map((item, index) => (
                    <img
                      key={index}
                      src={item}
                      alt={`Brand ${index}`}
                      className={`${
                        item === burgerIcon
                          ? "h-[27px] w-[48px]"
                          : item === pizzaIcon
                          ? "h-[37px] w-[66px]"
                          : item === noodlesIcon
                          ? "h-[75px] w-[75px]"
                          : "h-[40px] w-[40px]"
                      } object-contain`}
                    />
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-[#050505] dark:text-white text-center font-urbanist text-[32px] font-semibold leading-[125%] mb-[24px] transition-colors">
                  Upgrade to Duddus+
                </h2>

                <div className="flex gap-6 w-full mb-[24px] pb-[24px] border-b-2 dark:border-gray-700">
                  {PLANS.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlan(plan)}
                      className={`relative flex flex-col justify-center items-center gap-3 w-[183px] h-[167px] p-3 rounded-2xl transition-colors ${
                        selectedPlan.id === plan.id
                          ? "border-2 border-[#7402A7] dark:border-purple-600"
                          : "border border-[#E3E3E3] dark:border-gray-600"
                      } bg-gradient-to-r from-[rgba(252,74,26,0.15)] to-[rgba(247,183,51,0.15)] dark:from-[rgba(252,74,26,0.10)] dark:to-[rgba(247,183,51,0.10)]`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#7402A7] dark:bg-purple-600 text-white text-[15px] p-[7.5px] rounded-[8px] shadow leading-[15px] font-medium w-[108px]">
                          <p>Most Popular</p>
                        </div>
                      )}
                      <p className="text-lg font-medium text-gray-800 dark:text-gray-200">
                        {plan.label}
                      </p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">
                        ₹{plan.amount}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full mt-[32px] mb-6">
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full flex-1 pl-[24px] pr-[12px] py-[12px] rounded-[150px] bg-[#7402A7] dark:bg-purple-600 text-white font-urbanist text-[16px] font-semibold hover:bg-[#5a0282] dark:hover:bg-purple-700 transition-colors disabled:opacity-60"
            >
              {loading
                ? "Processing..."
                : `Subscribe · ₹${selectedPlan.amount}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upgrade;
